import { TextField } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { PageLoading } from "../../../common/components/Loading";
import { useActionRequest, useDataRequest } from "../../../common/hooks/api";
import { NetworkRequest } from "../../../common/models";
import { Api } from "../../../common/service/Api";
import { StandardLabel } from "../../components/input/Labels";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { usePrimaryOrganization } from "../../hooks";
import { Routes } from "../../root/routes";
import { WorkflowSetupPageWrapper } from "./WorkflowSetupPageWrapper";
import { Button, ExplanatoryCard } from "@truevault/aquilo";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../stay-compliant/PrivacyNoticeChecklistItems";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { DevInstructionsFinishedWorkflowSetupSlug } from "../stay-compliant/WorkflowsDashboardPage";

export const ProfilngOptOutInstructionsPage = () => {
  // Constants

  // State
  const history = useHistory();
  const [readyToRender, setReadyToRender] = useState(false);
  const [instructions, setInstructions] = useState(null);

  // Requests
  const [organization, orgRequest] = usePrimaryOrganization();

  const fetchedInstructionsApi = useCallback(
    () => Api.requestHandlingInstructions.getProfilingOptOutInstructions(organization.id),
    [organization.id],
  );
  const {
    result: fetchedInstructions,
    refresh: refreshInstruction,
    invalidate: invalidateInstructions,
    request: instructionsRequest,
    isSettled,
  } = useDataRequest({
    queryKey: ["instructions", organization.id],
    api: fetchedInstructionsApi,
  });

  const updateInstructionsApi = useCallback(
    () =>
      Api.requestHandlingInstructions.updateProfilingOptOutInstructions({
        organizationId: organization.id,
        requestHandlingInstructions: instructions,
      }),
    [organization.id, instructions],
  );
  const { fetch: updateInstructions, request: updateInstructionsRequest } = useActionRequest({
    api: updateInstructionsApi,
    onSuccess: () => {
      invalidateInstructions();
      refreshInstruction();
    },
  });

  const { setAnswer: setDevSurveyAnswer } = useSurvey(
    organization?.id,
    DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  );

  // Effects
  useEffect(() => {
    if (NetworkRequest.areFinished(orgRequest)) {
      setReadyToRender(true);
    }
  }, [orgRequest]);

  useEffect(() => {
    if (instructionsRequest.success && instructions === null && isSettled) {
      setInstructions(fetchedInstructions);
    }
  }, [fetchedInstructions, instructionsRequest, instructions, isSettled]);

  // Content

  if (!readyToRender || !organization) {
    return (
      <StandardPageWrapper>
        <PageLoading />
      </StandardPageWrapper>
    );
  }

  if (!organization.requiresProfilingOptOut) {
    return (
      <WorkflowSetupPageWrapper
        selectedTabKey="request-to-profiling-opt-out"
        nextContent="Done"
        nextOnClick={async () => {
          await setDevSurveyAnswer(DevInstructionsFinishedWorkflowSetupSlug, "true");
          history.push(Routes.workflows.dashboard);
        }}
        nextDisabled={false}
      >
        <div className="tw-boundary">
          <div className="text-neutral-600 font-semibold mt-12 max-w-[776px]">
            Based on your data map you do not require a profiling opt-out workflow.
          </div>
        </div>
      </WorkflowSetupPageWrapper>
    );
  }

  return (
    <WorkflowSetupPageWrapper
      selectedTabKey="request-to-profiling-opt-out"
      nextContent="Done"
      nextOnClick={async () => {
        await updateInstructions();
        await setDevSurveyAnswer(DevInstructionsFinishedWorkflowSetupSlug, "true");
        history.push(Routes.workflows.dashboard);
      }}
      nextDisabled={false}
    >
      <>
        <div className="tw-boundary">
          <div className="max-w-[924px]">
            <ExplanatoryCard
              topBody={
                <div>
                  <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3">
                    What is a Profiling Opt-Out Request?
                  </h3>
                  <div className="text-sm font-normal leading-5 text-gray-500 space-y-4">
                    <p>
                      A consumer's request that your business stop using their personal information
                      to create a profile about them that is used for certain high-impact automated
                      decision-making. Examples include using automated profiling to provide or deny
                      a person credit, housing, insurance, education, health care services, or
                      employment opportunities.
                    </p>
                  </div>
                </div>
              }
              expandable={false}
            />
            <p className="text-neutral-900 text-[18px] font-normal pt-4 px-2">
              When you receive a Request to Opt-Out of Automated Profiling, your business will need
              to discontinue using the consumer's personal data in automated ways that produce legal
              or similar effects (like determining creditworthiness or education opportunities).
            </p>
            <p className="text-neutral-900 text-[18px] font-normal mt-1 mb-2.5 px-2">
              Write instructions for your team to use when processing a Request to Opt-Out of
              Automated Profiling.
            </p>
          </div>
        </div>

        <div className="tw-boundary">
          <div className="px-2">
            <StandardLabel label="Instructions" subLabel=" (optional)" />
          </div>
          <TextField
            className="instructions--text-field"
            variant="outlined"
            value={instructions}
            onChange={({ target }) => setInstructions(target.value)}
            fullWidth={false}
            multiline={true}
            rows={8}
          />
        </div>
        {instructions !== null && fetchedInstructions != instructions && (
          <div className="tw-boundary">
            <div className="ml-[697px] mt-1">
              <Button
                color="primary"
                onClick={updateInstructions}
                disabled={updateInstructionsRequest.running}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </>
    </WorkflowSetupPageWrapper>
  );
};
